/**
 * Reads the serial number of a board over WebUSB.
 *
 * WebSerial does not expose it: getInfo() answers with the usb vendor and product
 * ids and nothing else. WebUSB does expose it, but the two APIs keep separate
 * permission stores and there is no identifier that ties a SerialPort to a
 * USBDevice, so a port and a device can only be matched by the choice of the user,
 * by the pair of events a plug produces, or by there being exactly one of each.
 *
 * Every answer carries how it was reached. The serial number is the key usage data
 * gets attached to, and a serial pinned on the wrong board corrupts the history of
 * two boards at once in a way nothing downstream can detect or undo, so when the
 * match cannot be made the answer is no serial rather than a guess.
 *
 * The module holds no bindings: callers re-resolve when their port list changes,
 * which is what lets an 'unambiguous' answer stop being given once a second board
 * of the same model shows up.
 */
import { BOARD_USB_IDS } from './boardUsbIds';

/**
 * Whether the serial can be trusted as belonging to that port. Only two answers,
 * because only two things are ever done with it: attach usage data to the board,
 * or fall back to the port description.
 *
 * A match is never guessed at, so everything that gets a serial gets it with the
 * board identified: the user pointed at it, the plug produced its pair of events,
 * or it was the only board of its model on both sides. Anything else is NONE.
 */
export const CONFIDENCE = {
  HIGH: 'high',
  NONE: 'none',
};

/**
 * How the port and the device were tied together. Nothing decides on this and no
 * caller should branch on it: it travels with the event so that a disagreement
 * found in the data later can be traced back to the way the match was made.
 */
/**
 * Why there is no serial number, which callers do have to tell apart: a board
 * whose descriptor carries none will never have one however many times it is
 * asked for, and neither will one read through a browser that fakes the answer,
 * while everything else is worth another try. Asking an older board again forever
 * is the one thing the step must not do.
 */
export const REASON = {
  // It was read, so there is no reason.
  NONE: 'none',
  // The board was identified and its descriptor carries no serial number. Every
  // board made before they were programmed in is like this.
  ABSENT: 'absent',
  // Nothing was read at all: the chooser was dismissed, the permission was not
  // given, no board could be matched, or WebUSB is not there.
  UNAVAILABLE: 'unavailable',
  /**
   * The browser answers with a serial it made up. See isSerialMasked: what comes
   * back is well-formed and indistinguishable from a real one, so this is the only
   * record that the read happened and was worthless.
   *
   * Apart from ABSENT because the board does have a serial, and apart from
   * UNAVAILABLE because no retry reaches it: the chooser answers the same way
   * every time, and only the user turning the protection off for this site
   * changes that.
   */
  MASKED: 'masked',
};

export const MATCH = {
  // The user pointed at the device in the browser's chooser.
  PICKER: 'picker',
  // One plug produced exactly one serial event and one usb event of that model.
  PLUG: 'plug',
  // Exactly one port and one device of that model, both present. The weakest of
  // the three: the permission stores are independent, so in principle the single
  // visible port and the single visible device could be different boards. It takes
  // permissions revoked by hand to arrange, since a usb grant is only ever given
  // to a board that was linked over serial first. A read taken before the board is
  // linked starts out as this and is only kept if the port side turns out to be
  // unambiguous as well.
  SINGLE: 'single',
  // The port is the usb device itself, so there was nothing to match.
  DEVICE: 'device',
  NONE: 'none',
};

// How long a serial event waits for the usb event of the same plug. The pair comes
// from one physical plug, so they land within a few dozen milliseconds of each
// other; the wait is bounded because the usb event never arrives for a board with
// no WebUSB permission, which is every board until it goes through the chooser
// once.
export const PAIRING_WINDOW_MS = 1500;

/**
 * The ids travel on every answer, and not only the ones that carry a serial. A
 * read done before the board is linked has to be tied to the port that turns up
 * afterwards, and the ids are what ties them; a board picked out of the chooser
 * whose descriptor has no serial still narrows the port chooser that follows.
 */
const noSerial = (reason = REASON.UNAVAILABLE, device = null) => ({
  serialNumber: null,
  confidence: CONFIDENCE.NONE,
  match: MATCH.NONE,
  reason,
  vendorId: device ? device.vendorId : null,
  productId: device ? device.productId : null,
});

const found = (device, match) => ({
  serialNumber: device.serialNumber,
  confidence: CONFIDENCE.HIGH,
  match,
  reason: REASON.NONE,
  vendorId: device.vendorId,
  productId: device.productId,
});

const usb = () => ((typeof navigator !== 'undefined' && navigator.usb) ? navigator.usb : null);
const serial = () => ((typeof navigator !== 'undefined' && navigator.serial) ? navigator.serial : null);

export const isSupported = () => !!usb();

/**
 * Whether the browser replaces the serial number of the descriptor with one of
 * its own before handing it over.
 *
 * Brave does, as an antifingerprinting measure: a usb serial is a stable global
 * identifier of a machine, so what a site reads is a pseudorandom value derived
 * from a per-session, per-eTLD+1 seed. It is well-formed and there is no way to
 * tell it apart from a real one by looking at it - a micro:bit whose descriptor
 * says 9905360200052833e767e8ed8ab4d23c000000006e052820 reads as GqVp7GDJMlxBIjRn -
 * so the browser has to be recognised instead of the value.
 *
 * Storing it would break the identity two ways at once, and both are the kind
 * nothing downstream can undo: it changes every time the browser is restarted, so
 * one board turns into a new board each session, and brave/brave-browser#30041
 * has it repeating across devices, so two boards merge into one history.
 *
 * navigator.brave.isBrave() is what Brave itself points sites at. It is not a
 * guarantee: Brave takes the api away on sites it considers to be discriminating
 * against it, and a user can hide it by hand, in which case a farbled serial gets
 * through as if it were real. Cheap, official and right in the common case is as
 * far as this goes; a check on the shape of the value would need a format per
 * board model, and BOARD_USB_IDS is still empty.
 *
 * Asked once and remembered: the answer cannot change while the page is open, and
 * every read goes through here.
 */
let maskedCheck = null;

export const isSerialMasked = () => {
  if (!maskedCheck) {
    maskedCheck = (async () => {
      try {
        const brave = (typeof navigator !== 'undefined') ? navigator.brave : null;
        return !!(brave && typeof brave.isBrave === 'function' && await brave.isBrave());
      } catch (error) {
        // An api that throws says nothing about the serial being faked, and a
        // browser reporting nothing is the ordinary case.
        return false;
      }
    })();
  }
  return maskedCheck;
};

/**
 * The agents carry the ids as the padded hex strings they show in the port name,
 * while WebUSB and WebSerial report them as numbers. 'unknown' stands for a port
 * whose descriptor has no usb ids, which can never be matched to a device.
 */
const toNumber = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && id !== 'unknown') {
    const parsed = parseInt(id, 16);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const matchesIds = (candidate, vendorId, productId) => (
  candidate.vendorId === vendorId && candidate.productId === productId
);

// A device is only useful here if it actually carries a serial number: the older
// boards share their vendor and product ids with the ones that have one.
const withSerial = (devices) => (devices || []).filter((device) => device && device.serialNumber);

const grantedDevices = async (vendorId, productId) => {
  const api = usb();
  if (!api) return [];
  try {
    const devices = await api.getDevices();
    return withSerial(devices).filter((device) => matchesIds(device, vendorId, productId));
  } catch (error) {
    return [];
  }
};

/**
 * The filters a chooser is opened with, from the ids of the boards. An empty list
 * is what WebUSB and WebSerial both read as no filter, offering everything, which
 * is exactly what an empty BOARD_USB_IDS is meant to mean.
 */
const boardFilters = () => BOARD_USB_IDS
  .map((ids) => ({ vendorId: toNumber(ids.vendorId), productId: toNumber(ids.productId) }))
  .filter((ids) => ids.vendorId !== null && ids.productId !== null);

/**
 * Whether the chooser is narrowed at all. While no ids have been collected the
 * two are the same dialog, so the step must not offer to lift a filter that is
 * not there: the user would press it, get the same list, and stop trusting
 * either button.
 */
export const hasBoardFilters = () => boardFilters().length > 0;

const isBoardDevice = (device) => {
  const filters = boardFilters();
  if (!filters.length) return true;
  return filters.some((ids) => matchesIds(device, ids.vendorId, ids.productId));
};

/**
 * Every granted device that could be a board, serial number or not. Unlike
 * grantedDevices this keeps the ones without a serial: they are what lets an
 * older board be asked about once and then recognised, instead of putting the
 * chooser in front of the user again every session.
 */
const grantedBoardDevices = async () => {
  const api = usb();
  if (!api) return [];
  try {
    const devices = await api.getDevices();
    return (devices || []).filter((device) => device && isBoardDevice(device));
  } catch (error) {
    return [];
  }
};

const grantedPortCount = async (vendorId, productId) => {
  const api = serial();
  if (!api) return 0;
  try {
    const ports = await api.getPorts();
    return (ports || []).filter((port) => {
      if (!port || typeof port.getInfo !== 'function') return false;
      const info = port.getInfo() || {};
      return info.usbVendorId === vendorId && info.usbProductId === productId;
    }).length;
  } catch (error) {
    return 0;
  }
};

/**
 * Devices seen arriving, kept only as long as a plug pairing could still use them.
 * Registered once for the whole page: an Agent is built per panel, and two panels
 * must not end up with two sets of listeners writing the same answers.
 */
let plugEvents = [];
let observing = false;

// Held for longer than the window itself, so an arrival that landed right as a
// window opened is still there when that window closes and counts the pair.
const forgetStalePlugEvents = (now) => {
  plugEvents = plugEvents.filter((event) => (now - event.at) <= (PAIRING_WINDOW_MS * 2));
};

export const observePlugEvents = () => {
  const api = usb();
  if (!api || observing) return;
  observing = true;
  api.addEventListener('connect', (event) => {
    const device = event.device || (event.target && event.target.device);
    if (device && device.serialNumber) {
      plugEvents.push({ device, at: Date.now() });
    }
  });
  api.addEventListener('disconnect', (event) => {
    const device = event.device || (event.target && event.target.device);
    plugEvents = plugEvents.filter((plug) => plug.device !== device);
  });
};

const pairedByPlug = (vendorId, productId) => {
  const now = Date.now();
  forgetStalePlugEvents(now);
  const matching = plugEvents.filter((plug) => matchesIds(plug.device, vendorId, productId));
  // Two boards of the same model arriving together cannot be told apart: which
  // serial event belongs to which usb event is not knowable.
  return (matching.length === 1) ? matching[0].device : null;
};

/**
 * Only one chooser at a time. A second caller joins the dialog already open
 * instead of asking the browser for another one, which it would refuse anyway.
 */
let promptInFlight = null;

/**
 * Asks the user to point at the board. Needs to run inside a user gesture, and the
 * promise stays pending for as long as the chooser is open: the wait is a person
 * making a decision, so it carries no deadline.
 *
 * The two ways of coming back empty are told apart, because they call for
 * different answers: a chooser that was dismissed is worth offering again, while a
 * board that was picked and carries no serial in its descriptor never will, and
 * asking again would leave the user stuck on a step that cannot succeed.
 */
export const requestSerialNumber = async ({ vendorID, productID } = {}) => {
  const api = usb();
  if (!api) return noSerial();
  // Answered before the chooser is opened. Asking the user to point at the board
  // and then throwing the answer away is worse than not asking.
  if (await isSerialMasked()) return noSerial(REASON.MASKED);

  const vendorId = toNumber(vendorID);
  const productId = toNumber(productID);
  // An empty filter list would offer every usb device on the machine; without ids
  // to narrow it down there is nothing worth asking for.
  if (vendorId === null || productId === null) return noSerial();

  if (!promptInFlight) {
    promptInFlight = api.requestDevice({ filters: [{ vendorId, productId }] })
      .catch(() => null)
      .finally(() => { promptInFlight = null; });
  }

  const device = await promptInFlight;
  if (!device) return noSerial(REASON.UNAVAILABLE);
  if (!device.serialNumber) return noSerial(REASON.ABSENT, device);
  return found(device, MATCH.PICKER);
};

/**
 * The serial side of a plug just fired. Answers with the device that arrived with
 * it, which is a certain pairing however many identical boards were already
 * connected before.
 *
 * The whole window is waited out rather than answering with the first arrival:
 * two boards of the same model plugged in together produce two usb events, and
 * which one belongs to this serial event is not knowable. Answering early would
 * hand back one of them as a certain match, so instead the arrivals are counted
 * once the window closes and anything other than exactly one answers with no
 * serial. A board with no WebUSB permission produces no arrival at all, which is
 * why the wait is bounded.
 */
export const resolveOnPlug = ({ vendorID, productID } = {}, { window = PAIRING_WINDOW_MS } = {}) => {
  const api = usb();
  const vendorId = toNumber(vendorID);
  const productId = toNumber(productID);
  if (!api || vendorId === null || productId === null) return Promise.resolve(noSerial());

  observePlugEvents();

  return new Promise((resolve) => {
    setTimeout(() => {
      // Asked inside the timer and not before it, so the wait is registered in the
      // same tick as the call: a caller driving the clock by hand advances it
      // straight away, and a window that did not exist yet would never close.
      isSerialMasked().then((masked) => {
        if (masked) return resolve(noSerial(REASON.MASKED));
        const paired = pairedByPlug(vendorId, productId);
        return resolve(paired ? found(paired, MATCH.PLUG) : noSerial());
      });
    }, window);
  });
};

/**
 * No plug to correlate and no gesture to prompt with: the page opened with the
 * board already connected, which is the common case. Counts both sides itself
 * rather than trusting a caller's view, since an agent only sees the ports of its
 * own transport.
 *
 * One port and one device of that model is the only case that gets an answer.
 * Anything else answers with no serial, several boards that all carry one
 * included: which of them sits behind which port is not knowable here, and the
 * board is never asked about, so a wrong guess would go unnoticed.
 */
export const resolveFromGrants = async ({ vendorID, productID } = {}) => {
  const api = usb();
  const vendorId = toNumber(vendorID);
  const productId = toNumber(productID);
  if (!api || vendorId === null || productId === null) return noSerial();
  if (await isSerialMasked()) return noSerial(REASON.MASKED);

  const devices = await grantedDevices(vendorId, productId);
  if (devices.length !== 1) return noSerial();

  const ports = await grantedPortCount(vendorId, productId);
  if (ports !== 1) return noSerial();

  return found(devices[0], MATCH.SINGLE);
};

/**
 * The board has not been linked yet, so there is no port and no ids to narrow the
 * chooser with. Answers from the grants alone, without asking: one granted board
 * device present is the only case that gets an answer, and a granted device with
 * no serial in its descriptor answers ABSENT, which is what stops an older board
 * from being asked about again every session.
 *
 * The match is only half made here - there is no port to count against yet - and
 * matchStagedRead makes the other half when the port turns up.
 */
export const resolveBeforeLink = async () => {
  const api = usb();
  if (!api) return noSerial();
  if (await isSerialMasked()) return noSerial(REASON.MASKED);

  const devices = await grantedBoardDevices();
  if (devices.length !== 1) return noSerial();

  const device = devices[0];
  if (!device.serialNumber) return noSerial(REASON.ABSENT, device);
  return found(device, MATCH.SINGLE);
};

/**
 * Asks the user to point at the board before it is linked. Needs a user gesture,
 * and the promise stays pending for as long as the chooser is open: the wait is a
 * person making a decision, so it carries no deadline.
 *
 * Narrowed to the ids of the boards, which is all there is to narrow it by at
 * this point. While that list is empty the chooser offers every usb device on the
 * machine anyway.
 *
 * `unfiltered` lifts that narrowing, for the board whose ids are not in the list
 * yet and which the filter therefore hides. It costs nothing in identity: the ids
 * of whatever is picked are checked against the port in matchStagedRead either
 * way, so the filter is what keeps the list short, not what keeps the answer
 * honest. What it cannot do is show a device WebUSB has no interface to reach -
 * that one is absent from both dialogs.
 */
export const requestBeforeLink = async ({ unfiltered = false } = {}) => {
  const api = usb();
  if (!api) return noSerial();
  if (await isSerialMasked()) return noSerial(REASON.MASKED);

  if (!promptInFlight) {
    promptInFlight = api.requestDevice({ filters: unfiltered ? [] : boardFilters() })
      .catch(() => null)
      .finally(() => { promptInFlight = null; });
  }

  const device = await promptInFlight;
  if (!device) return noSerial(REASON.UNAVAILABLE);
  if (!device.serialNumber) return noSerial(REASON.ABSENT, device);
  return found(device, MATCH.PICKER);
};

/**
 * Whether a read taken before the board was linked belongs to the port that has
 * just turned up. The ids have to agree in every case: the flow opens two
 * choosers and nothing stops a user from pointing at one board in the first and
 * another in the second.
 *
 * A read the user made in the chooser needs nothing more, the ids agreeing being
 * as much as can be checked once two deliberate choices have been made. One
 * resolved from the grants without asking needs the port side to be unambiguous
 * too, which is the half of MATCH.SINGLE that could not be checked before the
 * port existed.
 */
export const matchStagedRead = (staged, { vendorID, productID, sameIdPortCount = 1 } = {}) => {
  /**
   * A read with nothing in it keeps the reason it came with. Why it came back
   * empty belongs to the read and not to the matching - a browser that fakes
   * serials fakes them for every port, and a descriptor with none has none
   * whichever port it is held against - and this is the answer that gets written
   * on the port, so flattening it here is what the event ends up carrying.
   *
   * Only the reason travels: there is no serial to pin on anything, so nothing
   * can be put on the wrong board by it. 'none' is not carried because a reason
   * of 'none' with no serial says a serial was read, which would be a lie in the
   * data rather than a missing field.
   */
  if (!staged || !staged.serialNumber) {
    const reason = staged && staged.reason;
    return noSerial((reason && reason !== REASON.NONE) ? reason : REASON.UNAVAILABLE);
  }

  const vendorId = toNumber(vendorID);
  const productId = toNumber(productID);
  if (vendorId === null || productId === null) return noSerial();
  if (staged.vendorId !== vendorId || staged.productId !== productId) return noSerial();

  if (staged.match === MATCH.PICKER) return staged;
  if (staged.match === MATCH.SINGLE && sameIdPortCount === 1) return staged;
  return noSerial();
};

// Exposed for the tests, which need each case to start from a known state.
export const resetForTests = () => {
  plugEvents = [];
  observing = false;
  promptInFlight = null;
  maskedCheck = null;
};
